import {
  EcgClassification,
  EcgDeterminationApi,
  IEcgRecordingApi,
  IEcgRecordingSummary,
} from "../../@types/Ecg";
import { getEcgClassificationLabel } from "./ecgClassification";

const DETERMINATIONS: Record<EcgDeterminationApi, EcgClassification> = {
  Normal: "normal",
  SinusRhythm: "sinusRhythm",
  SinusRhythmPacs: "sinusRhythmPacs",
  SinusRhythmWideQrs: "sinusRhythmWideQrs",
  SinusRhythmPvcs: "sinusRhythmPvcs",
  AtrialFibrillation: "atrialFibrillation",
  Tachycardia: "tachycardia",
  Bradycardia: "bradycardia",
  Short: "short",
  Long: "long",
  Unclassified: "unclassified",
  Unreadable: "unreadable",
  NoAnalysis: "noAnalysis",
  Unknown: "unknown",
};

// Map the API's PascalCase determination to our internal classification key.
export function mapEcgDetermination(
  determination: EcgDeterminationApi | string | null | undefined
): EcgClassification {
  if (!determination) return "unknown";

  return DETERMINATIONS[determination as EcgDeterminationApi] ?? "unknown";
}

// Map an API recording (metadata only) to a display-ready summary.
export function mapEcgSummary(api: IEcgRecordingApi): IEcgRecordingSummary {
  const classification = mapEcgDetermination(api.evaluation?.determination);

  return {
    id: api.id,
    classification,
    determinationTitle:
      api.evaluation?.summary?.shortTitle ??
      getEcgClassificationLabel(classification),
    determinationDescription: api.evaluation?.summary?.description ?? "",
    recordedAt: api.recordedAt,
    durationSeconds: Math.round((api.durationMs ?? 0) / 1000),
    leadCount:
      api.configuration?.leadCount ?? api.configuration?.leads.length ?? 0,
    leads: api.configuration?.leads ?? [],
    sampleRateHz: api.configuration?.sampleRateHz ?? 300,
    amplitudeUnit: api.configuration?.amplitudeUnit ?? "mV",
    // Null (not 1) when the endpoint omits a scale, so the parser falls back to
    // the scale embedded in the waveform JSON (e.g. the device's ADC gain)
    // instead of treating raw ADC samples as if they were already millivolts.
    amplitudeScale: api.configuration?.amplitudeScale ?? null,
    averageHeartRateBpm: api.evaluation?.averageHeartRateBpm ?? null,
    batteryPercent: api.device?.batteryLevelPercent ?? null,
    platform: api.source?.platform ?? "",
    waveformUrl: api.artifacts?.waveformUrl ?? null,
  };
}
